import React from "react";
import {
  Box,
  ButtonBase,
  Divider,
  Paper,
  Slider,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { colors } from "src/config/colors";
import { UseFormReturn } from "react-hook-form";
import { CategoryBrandFilters } from "src/models/categoryBrand/types";
import Filter from "src/components/BrandCategoryPage/components/MainBrandSection/components/Filters/Filter";
import SubmitButton from "src/components/common/Buttons/SubmitButton";
import { RequestParams } from "src/components/BrandCategoryPage/hooks/useCategoryBrand";
import ActiveFilterCircle from "src/assets/icons/ActiveFilterCircle";
import { Controller } from "react-hook-form";
import OptionalModal from "src/components/common/OptionalModal/OptionalModal";
import { ModalType } from "src/utils/hooks/state/useModal";
import { useModalContext } from "src/components/common/Layout/context/ModalContext";
import { Theme } from "@mui/system";

export interface FilterFields {
  forms: string[];
  dosages: string[];
  packagins: string[];
  priceFilter: [number | undefined, number | undefined] | undefined;
}

export interface FiltersProps {
  filters?: CategoryBrandFilters;
  requestBrandsSubmit: (params: RequestParams) => void;
  formMethods: UseFormReturn<FilterFields, object>;
}

export const MIN_PRICE_LIMIT = 0;
export const MAX_PRICE_LIMIT = 99999;

const FiltersList: React.FC<FiltersProps> = ({
  filters,
  requestBrandsSubmit,
  formMethods,
}) => {
  const { modalType, closeModal } = useModalContext();
  const isActiveModal = modalType === ModalType.BRAND_PAGE_FILTERS_MODAL;

  const { handleSubmit, reset, control } = formMethods;

  /*const formValues = useWatch({ control });

  const debounceFormValues = useDebounceValue(formValues, 1000);

  useEffect(() => {
    requestBrandsSubmit({ filters: debounceFormValues });
    console.log(debounceFormValues);
  }, [debounceFormValues]);*/

  const activeFilters =
    formMethods.getValues().forms.length ||
    formMethods.getValues().packagins.length ||
    formMethods.getValues().dosages.length;

  const onSubmit = (fields: FilterFields) => {
    requestBrandsSubmit({ filters: fields });
  };

  const isLargeScreenDown = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("lg")
  );
  const isSmallScreenDown = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  return (
    <OptionalModal
      isOpen={isActiveModal}
      isModal={isLargeScreenDown}
      onCloseModal={closeModal}
      fullScreen={isSmallScreenDown}
    >
      <Paper
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          width: isActiveModal ? "100%" : "270px",
          boxShadow: isActiveModal
            ? "none"
            : "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
          height: "100%",
          mr: isActiveModal ? "0px" : "24px",
          padding: isActiveModal ? "0px 4px" : "24px",
          borderRadius: "8px",
          border: "0px",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: isActiveModal ? "center" : "space-between",
            alignItems: "center",
            width: "100%",
            mb: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              position="relative"
              height="16px"
              sx={{
                display: isActiveModal ? "none" : "block",
              }}
            >
              <Box
                sx={{
                  display: activeFilters ? "block" : "none",
                  position: "absolute",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: colors.yellow,
                  right: 0,
                }}
              />
              <ActiveFilterCircle />
            </Box>
            <Typography
              sx={{
                ml: isActiveModal ? 0 : 1,
                fontSize: { xs: "16px", md: "18px" },
                color: colors.black2,
                fontWeight: 500,
              }}
            >
              Фильтры
            </Typography>
          </Box>
          <ButtonBase
            sx={{
              display: isActiveModal ? "none" : "block",
              fontSize: "16px",
              color: colors.blue2,
              fontWeight: 400,
            }}
            onClick={() => {
              reset();
              handleSubmit(onSubmit)();
            }}
          >
            Сбросить
          </ButtonBase>
        </Box>
        <Controller
          name="priceFilter"
          control={control}
          render={({ field: { value, onChange } }) => {
            const handleChange = (
              event: Event,
              newValue: number | number[]
            ) => {
              onChange(newValue as number[]);
            };

            const handleMinPriceInputChange = (
              event: React.ChangeEvent<HTMLInputElement>
            ) => {
              const newValue = event.target.value
                ? Number(event.target.value)
                : undefined;

              onChange([newValue, value?.[1]]);
            };

            const handleMaxPriceInputChange = (
              event: React.ChangeEvent<HTMLInputElement>
            ) => {
              const newValue = event.target.value
                ? Number(event.target.value)
                : undefined;

              onChange([value?.[0], newValue]);
            };

            const handleMaxPriceBlur = () => {
              if (value?.[0] && value?.[1] && value?.[0] >= value?.[1]) {
                onChange([value[0], value[0]]);
              }
            };

            const handleMinPriceBlur = () => {
              if (value?.[0] && value?.[1] && value?.[0] >= value?.[1]) {
                onChange([value[1], value[1]]);
              }
            };

            return (
              <Box sx={{ width: "100%" }}>
                <Typography
                  sx={{
                    mb: 3,
                    fontSize: { xs: "14px", md: "16px" },
                    color: colors.black2,
                  }}
                >
                  Цена, ₽
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <TextField
                    InputProps={{
                      startAdornment: (
                        <Typography
                          sx={{
                            fontSize: "14px",
                            mr: 1,
                            color: colors.black2,
                          }}
                        >
                          От
                        </Typography>
                      ),
                    }}
                    placeholder="0"
                    value={value?.[0] ?? ""}
                    onChange={handleMinPriceInputChange}
                    onBlur={handleMinPriceBlur}
                    size="small"
                    inputProps={{
                      step: 1,
                      type: "number",
                    }}
                    sx={{
                      ".MuiOutlinedInput-root": {
                        fontSize: "14px",
                        borderRadius: "8px",
                        width: { xs: "150px", tb: "103px" },
                        height: "48px",
                        "& input[type=number]::-webkit-outer-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                        "& input[type=number]::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      },
                    }}
                  />
                  <TextField
                    InputProps={{
                      startAdornment: (
                        <Typography
                          sx={{
                            fontSize: "14px",
                            mr: 1,
                            color: colors.black2,
                          }}
                        >
                          До
                        </Typography>
                      ),
                    }}
                    placeholder="99999"
                    value={value?.[1] ?? ""}
                    onChange={handleMaxPriceInputChange}
                    onBlur={handleMaxPriceBlur}
                    size="small"
                    inputProps={{
                      step: 1,
                      type: "number",
                    }}
                    sx={{
                      ".MuiOutlinedInput-root": {
                        fontSize: "14px",
                        borderRadius: "8px",
                        width: { xs: "150px", tb: "103px" },
                        height: "48px",
                        "& input[type=number]::-webkit-outer-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                        "& input[type=number]::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    paddingX: isActiveModal ? "8px" : "0px",
                  }}
                >
                  <Slider
                    value={[
                      value?.[0] || MIN_PRICE_LIMIT,
                      value?.[1] || MAX_PRICE_LIMIT,
                    ]}
                    max={MAX_PRICE_LIMIT}
                    min={MIN_PRICE_LIMIT}
                    onChange={handleChange}
                    valueLabelDisplay="auto"
                    sx={{
                      ".MuiSlider-track": {
                        backgroundColor: colors.blue2,
                        border: "1px solid #3C56CB",
                      },
                      ".MuiSlider-thumb": {
                        width: "16px",
                        height: "16px",
                        border: "2px solid #3C56CB",
                        borderRadius: "50%",
                        backgroundColor: "#3C56CB",
                      },
                    }}
                  />
                </Box>
              </Box>
            );
          }}
        />
        <Box>
          <Divider sx={{ my: 1 }} />
          {filters && (
            <>
              {filters.forms.length && (
                <Filter
                  title="Форма выпуска"
                  controllerName="forms"
                  control={control}
                  filterData={filters.forms}
                />
              )}
              {filters.dosages.length && (
                <Filter
                  title="Дозировка"
                  controllerName="dosages"
                  control={control}
                  filterData={filters.dosages}
                />
              )}
              {filters.packagins.length && (
                <Filter
                  title="Объем упаковки"
                  controllerName="packagins"
                  control={control}
                  filterData={filters.packagins}
                />
              )}
            </>
          )}
        </Box>{" "}
        <Box pb={2}>
          <SubmitButton
            onClick={closeModal}
            type="submit"
            sx={{
              width: isActiveModal ? "100%" : "222px",
              minHeight: "48px",
              mb: isActiveModal ? 1 : 0,
            }}
            title="Применить"
          />
          <SubmitButton
            onClick={() => {
              reset();
              handleSubmit(onSubmit)();
            }}
            sx={{
              display: isActiveModal ? "block" : "none",
              width: "100%",
              minHeight: "48px",
              backgroundColor: "transparent",
              border: "1px solid #F7E64D",
              borderRadius: "8px",
              ":hover": {
                backgroundColor: "transparent",
                opacity: "0.7",
              },
            }}
            title="Сбросить фильтры"
          />
        </Box>
      </Paper>
    </OptionalModal>
  );
};

export default FiltersList;
